var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ArrowUpward, CheckCircleRounded, ErrorRounded } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setInspectionSelectedItem, setInspectionSortSelectedItem } from 'app/actions/checklist';
import { Autosizer, Scrollbar } from 'app/components';
import React, { createContext, forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const ChecklistInspectionListContext = createContext(null);
const ChecklistInspectionIcons = {
    1: React.createElement(CheckCircleRounded, { fontSize: 'small', color: 'success' }),
    0: React.createElement(ErrorRounded, { fontSize: 'small', color: 'error' }),
};
const ChecklistInspectionIcon = ({ status }) => {
    const Component = ChecklistInspectionIcons[status];
    return Component ? Component : null;
};
const ChecklistInspectionList = () => {
    const dispatch = useDispatch();
    const { inspectionItems, inspectionSelectedItem, inspectionSearchValue, inspectionSortSelectedItem, inspectionSelectedFilters } = useSelector((state) => state.checklist);
    const sortItems = useMemo(() => {
        const compareFn = (a, b) => {
            var _a, _b, _c, _d;
            switch (inspectionSortSelectedItem.name) {
                case 'created_at':
                    switch (inspectionSortSelectedItem.order) {
                        case 'ASC':
                            return new Date(String(a[inspectionSortSelectedItem.name])).getTime() - new Date(String(b[inspectionSortSelectedItem.name])).getTime();
                        case 'DESC':
                            return new Date(String(b[inspectionSortSelectedItem.name])).getTime() - new Date(String(a[inspectionSortSelectedItem.name])).getTime();
                    }
                    break;
                case 'number':
                case 'trailer':
                case 'group_name':
                case 'employee':
                    switch (inspectionSortSelectedItem.order) {
                        case 'ASC':
                            return String((_a = a[inspectionSortSelectedItem.name]) !== null && _a !== void 0 ? _a : '').localeCompare(String((_b = b[inspectionSortSelectedItem.name]) !== null && _b !== void 0 ? _b : ''), undefined, {
                                sensitivity: 'base',
                            });
                        case 'DESC':
                            return String((_c = b[inspectionSortSelectedItem.name]) !== null && _c !== void 0 ? _c : '').localeCompare(String((_d = a[inspectionSortSelectedItem.name]) !== null && _d !== void 0 ? _d : ''), undefined, {
                                sensitivity: 'base',
                            });
                    }
                    break;
                case 'status':
                    switch (inspectionSortSelectedItem.order) {
                        case 'ASC':
                            return Number(a.status) - Number(b.status);
                        case 'DESC':
                            return Number(b.status) - Number(a.status);
                    }
                    break;
            }
        };
        const filterByFilters = (item) => {
            const checkFilter = (filterName) => {
                const filter = inspectionSelectedFilters[filterName];
                switch (filterName) {
                    case 'status':
                    case 'template_id':
                    case 'employee_id':
                    case 'transport_id':
                        return filter.length === 0 || filter.includes(item[filterName]);
                    case 'from':
                        return !filter || moment(item.from).isSameOrAfter(filter);
                    case 'to':
                        return !filter || moment(item.to).isSameOrBefore(filter);
                }
            };
            return Object.keys(inspectionSelectedFilters).every((filterName) => checkFilter(filterName));
        };
        const filterBySearch = inspectionSearchValue.length
            ? inspectionItems.filter((item) => {
                var _a, _b;
                return String((_a = item.number) !== null && _a !== void 0 ? _a : '')
                    .toLowerCase()
                    .indexOf(inspectionSearchValue.toLowerCase()) !== -1 ||
                    String((_b = item.employee) !== null && _b !== void 0 ? _b : '')
                        .toLowerCase()
                        .indexOf(inspectionSearchValue.toLowerCase()) !== -1;
            })
            : inspectionItems;
        return filterBySearch.filter(filterByFilters).sort(compareFn);
    }, [inspectionItems, inspectionSearchValue, inspectionSortSelectedItem, inspectionSelectedFilters]);
    const handleItemClick = (value) => () => dispatch(setInspectionSelectedItem(value));
    const handleSortClick = (value) => () => dispatch(setInspectionSortSelectedItem(value));
    return (React.createElement(Box, { component: 'div', sx: (theme) => ({
            width: '100%',
            height: '100%',
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-horizontal': {
                right: '10px',
            },
            '.simplebar-vertical': {
                top: '30px',
                bottom: '0',
            },
            '.MuiListItem-root': {
                width: '100%',
                userSelect: 'none',
                padding: theme.spacing(0),
                borderRadius: '0',
            },
        }) },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(ChecklistInspectionListContext.Provider, { value: {
                    sortItemClick: handleSortClick,
                    sortSelectedItem: inspectionSortSelectedItem,
                } },
                React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 24, overscanCount: 10, itemCount: sortItems.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: InspectionListInnerElementType, itemData: {
                        items: sortItems,
                        selectedItem: inspectionSelectedItem,
                        onClick: handleItemClick,
                    } }, InspectionListItemRender)))))))));
};
const InspectionListInnerElementType = forwardRef((_a, ref) => {
    var { children, style } = _a, rest = __rest(_a, ["children", "style"]);
    return (React.createElement(ChecklistInspectionListContext.Consumer, null, (context) => {
        if (!context) {
            return null;
        }
        const { sortItemClick, sortSelectedItem } = context;
        return (React.createElement(List, Object.assign({ ref: ref, disablePadding: true, sx: {
                height: style === null || style === void 0 ? void 0 : style.height,
                minHeight: '100%',
            } }, rest),
            React.createElement(ListItem, { sx: (theme) => ({
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: style === null || style === void 0 ? void 0 : style.height,
                    minHeight: '100%',
                    zIndex: '2',
                    pointerEvents: 'none',
                    padding: theme.spacing(0),
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        flex: 'none',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            height: '100%',
                            bgcolor: theme.palette.divider,
                            width: '1px',
                            zIndex: '1',
                            pointerEvents: 'none',
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '&:before': {
                                display: 'none',
                            },
                        },
                    },
                }) }, config.inspectionColumns.map((column) => (React.createElement(ListItemText, { key: column.name, style: { width: column.width } })))),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    width: '100%',
                    display: 'flex',
                    position: 'sticky !important',
                    top: '0',
                    zIndex: '1',
                    '.MuiListItemButton-root': {
                        display: 'flex',
                        flex: 'none',
                        padding: theme.spacing(0),
                        position: 'relative',
                        bgcolor: 'background.default',
                        borderColor: theme.palette.divider,
                        '&:first-of-type': {
                            '.MuiListItemText-root': {
                                paddingLeft: theme.spacing(2),
                            },
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '.MuiListItemText-root': {
                                paddingRight: theme.spacing(2),
                            },
                        },
                        '.MuiTouchRipple-rippleVisible': {
                            color: 'inherit',
                        },
                        '.MuiListItemText-root': {
                            display: 'flex',
                            cursor: 'pointer',
                            margin: theme.spacing(0),
                            padding: '6px 6px',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                display: 'flex',
                                '&:nth-of-type(1)': {
                                    color: 'text.secondary',
                                    marginRight: '4px',
                                },
                                '&:nth-of-type(2) svg': {
                                    fontSize: '12px',
                                    transition: 'all 300ms',
                                    transform: 'rotateZ(0deg)',
                                },
                            },
                        },
                    },
                }) }, config.inspectionColumns.map((column) => (React.createElement(ListItemButton, { key: column.name, onClick: sortItemClick(column), disableTouchRipple: !column.order, sx: (theme) => ({
                    minWidth: column.width,
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        '.MuiTypography-root': {
                            width: 'auto',
                            '&:nth-of-type(2)': Object.assign({ opacity: '0.2', transform: 'rotateZ(0)', transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms' }, (sortSelectedItem.name === column.name && Object.assign({ opacity: '1' }, (sortSelectedItem.order === 'DESC' && {
                                transform: 'rotateZ(180deg)',
                            })))),
                        },
                    },
                }) },
                React.createElement(ListItemText, { disableTypography: true },
                    React.createElement(Typography, null, column.label),
                    React.createElement(Typography, null, column.order && React.createElement(ArrowUpward, { fontSize: 'small' }))))))),
            children));
    }));
});
InspectionListInnerElementType.displayName = 'InspectionListInnerElementType';
const InspectionListItemRender = ({ data, index, style }) => {
    const { items, selectedItem, onClick } = data;
    return (React.createElement(ListItem, { key: index, disablePadding: true, style: style, selected: items[index].id === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id), onClick: onClick(items[index]), sx: (theme) => (Object.assign({ width: '100%', marginTop: '30px', userSelect: 'none', borderRadius: '0', '&:hover': {
                '.MuiListItemText-root': {
                    cursor: 'pointer',
                },
            }, '&.Mui-selected': {
                '.MuiListItemText-root': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                '.MuiCheckbox-root': {
                    color: theme.palette.primary.contrastText,
                    '&.Mui-checked': {
                        color: theme.palette.primary.contrastText,
                    },
                },
            }, '.MuiListItemText-root': {
                display: 'flex',
                flex: 'none',
                height: '100%',
                margin: theme.spacing(0),
                padding: theme.spacing(0.5, 1),
                alignItems: 'center',
                '&:first-of-type': {
                    paddingLeft: theme.spacing(2),
                    svg: {
                        fontSize: '16px',
                        marginRight: '3px',
                    },
                },
                '&:last-of-type': {
                    flex: '1',
                    paddingRight: theme.spacing(2),
                },
                '.MuiTypography-root': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
                '.MuiCheckbox-root': {
                    padding: theme.spacing(0),
                    marginLeft: '-2px',
                    svg: {
                        fontSize: '18px',
                    },
                },
            } }, ((items.length - (items.length - 1 - index)) % 2 === 0 && {
            '& .MuiListItemText-root': {
                bgcolor: 'background.default',
            },
        }))) }, config.inspectionColumns.map((column) => {
        var _a;
        return (React.createElement(ListItemText, { key: column.name, sx: Object.assign({ minWidth: column.width }, (column.name === 'status' && {
                justifyContent: 'center',
                '.MuiTypography-root': {
                    display: 'flex',
                },
            })) }, column.name === 'status' ? (React.createElement(ChecklistInspectionIcon, { status: items[index].status })) : (React.createElement(Typography, null, column.format ? column.format(items[index][column.name]) : String((_a = items[index][column.name]) !== null && _a !== void 0 ? _a : '')))));
    })));
};
export default ChecklistInspectionList;
